import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LeaderboardEntry, LeaderboardGroupBy, LeaderboardMetric, ReportFilters } from '../../core/models/report.model';
import { OrgSettingsService } from '../../core/services/org-settings.service';
import { PermissionService } from '../../core/services/permission.service';
import { ReportsService } from '../../core/services/reports.service';
import { TranslationService } from '../../core/services/translation.service';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { ReportFilterBarComponent } from '../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface MetricGroupModel {
  metric: LeaderboardMetric;
  groupBy: LeaderboardGroupBy;
}

/**
 * "Leads or doors this day/week/month by user and by team, ranked by count or estimated
 * value, with an org setting to disable it" (design doc §8) -- "one more query over the
 * same data" is why it's built right alongside reports.
 */
@Component({
  selector: 'app-leaderboard',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './leaderboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './leaderboard.component.css',
})
export class LeaderboardComponent implements OnInit {
  private reportsService = inject(ReportsService);
  private orgSettingsService = inject(OrgSettingsService);
  private permissionService = inject(PermissionService);
  private translate = inject(TranslationService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canManageSettings = this.permissionService.has('org.settings');

  enabled = signal(true);
  entries = signal<LeaderboardEntry[]>([]);
  loading = signal(false);
  private lastFilters: ReportFilters | null = null;

  metricGroupForm = new FormGroup({});
  metricGroupModel: MetricGroupModel = { metric: 'doors', groupBy: 'user' };
  metricGroupFields: FormlyFieldConfig[] = [
    {
      key: 'metric',
      type: 'native-select',
      props: {
        label: this.translate.translate('leaderboard.metric'),
        showLabel: true,
        options: [
          { value: 'doors', label: this.translate.translate('leaderboard.doors') },
          { value: 'leads', label: this.translate.translate('leaderboard.leads') },
        ],
      },
    },
    {
      key: 'groupBy',
      type: 'native-select',
      props: {
        label: this.translate.translate('leaderboard.groupBy'),
        showLabel: true,
        options: [
          { value: 'user', label: this.translate.translate('reports.rateTrends.byUser') },
          { value: 'team', label: this.translate.translate('reports.rateTrends.byTeam') },
        ],
      },
    },
  ];

  constructor() {
    this.metricGroupForm.valueChanges.subscribe(() => this.reload());
  }

  async ngOnInit(): Promise<void> {
    this.enabled.set(await this.orgSettingsService.isLeaderboardEnabled());
  }

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.lastFilters = filters;
    await this.reload();
  }

  private async reload(): Promise<void> {
    if (!this.lastFilters || !this.enabled()) return;
    this.loading.set(true);
    try {
      this.entries.set(
        await this.reportsService.getLeaderboard(this.lastFilters, this.includeAllUsers, this.metricGroupModel.metric, this.metricGroupModel.groupBy),
      );
    } finally {
      this.loading.set(false);
    }
  }
}
